import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../../../../components/buttons/button";
import { CopyButton } from "../../../../components/buttons/copyButton";
import Dropdown from "../../../../components/dropdown/dropdown";
import { EventCard } from "../../../../components/event/eventCard";
import { Icon } from "../../../../components/icon/icon";
import { EventMoreOptionsModal } from "../../../../components/modal/app_modals/eventMoreOptionsModal";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import { useUpdateEventStatusMutation } from "../../../../redux/features/event/event.api";
import { CreatedEvent } from "../../../../redux/features/event/event.types";
import {
  formatDate,
  formatTime,
  getEventUrl,
  handleShare,
  parseTimeToDate,
} from "../../../../utils/helpers";
import { EventPermissions } from "../../../../utils/types";

export const EventDetailsCard = ({
  event,
  userEventPermissions,
  guestCount,
}: {
  event?: CreatedEvent;
  userEventPermissions: EventPermissions[];
  guestCount: number;
}) => {
  const navigate = useNavigate();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const [updateStatus, { isLoading: updatingStatus }] =
    useUpdateEventStatusMutation();

  const moreOptions = [
    {
      name: (
        <p className="flex items-center gap-2">
          <Icon svg="pencil" width={20} height={20} className="text-grey-400" />{" "}
          Edit Event Details
        </p>
      ),
      onClick: () => {
        navigate(`/event/${event?.slug}/edit`, {
          replace: true,
        });
      },
    },
    {
      name: (
        <p className="flex items-center gap-2">
          <Icon svg="share" width={20} height={20} className="text-grey-400" />{" "}
          Go To Event
        </p>
      ),
      onClick: () => {
        window.open(getEventUrl(event?.slug!), "_blank");
      },
    },
    {
      name: (
        <p className="flex items-center gap-2">
          <Icon svg="upload" width={20} height={20} className="text-grey-400" />{" "}
          Share Event
        </p>
      ),
      onClick: async () => {
        await handleShare("", "", getEventUrl(event?.slug ?? ""));
      },
    },
  ];

  return (
    <>
      <EventCard
        image={event?.image!}
        name={event?.name!}
        location={event?.location!}
        startDate={formatDate(event?.startDate!, "ddd, MMMM DD, YYYY", true)}
        endDate={formatDate(event?.endDate!, "ddd, MMMM DD, YYYY", true)}
        startTime={formatTime(parseTimeToDate(event?.startTime!))}
        endTime={formatTime(parseTimeToDate(event?.endTime!))}
        noTickets={undefined}
        totalGuests={guestCount}
        isOrganizer
        event={event!}
        className=""
        imageDimension={400}
        showStatusPill
      >
        <div className="grid flex-1 grid-cols-1 flex-col justify-between gap-4">
          {event?.status !== "completed" ? (
            <div className="flex flex-row items-center gap-[10px]">
              {event?.status !== "archived" ? (
                <>
                  <Button
                    type="button"
                    onClick={() => {
                      navigate(`/event/${event?._id}/guests`);
                    }}
                    text="Check-In Guests"
                    icon="camera"
                    variation="plain-grey"
                    className=""
                    disabled={!userEventPermissions.includes("checkInGuests")}
                  />

                  <Button
                    type="button"
                    onClick={() => {
                      navigate(`/event/${event?.slug}/edit`, {
                        replace: true,
                      });
                    }}
                    text="Edit Event Details"
                    icon="pencil"
                    variation="plain-grey"
                    className="max-md:hidden"
                    disabled={
                      !userEventPermissions.includes("editEventDetails")
                    }
                  />
                </>
              ) : undefined}

              {userEventPermissions.includes("archiveEvent") &&
              event?.status === "archived" ? (
                <Button
                  type="button"
                  onClick={async () => {
                    const resp = await updateStatus({
                      id: event?._id,
                      payload: {
                        status: "unarchive",
                      },
                    });

                    if (resp.data?.status === "success") {
                      navigate("/events?EventStatus=completed");
                      toast.success("Event removed from archives");
                    }
                  }}
                  text="Unarchive Event"
                  icon="archive"
                  variation="plain-grey"
                  className=""
                  disabled={updatingStatus}
                  loading={updatingStatus}
                />
              ) : undefined}

              {event?.status !== "archived" ? (
                <Dropdown
                  menuItems={moreOptions.slice(1)}
                  menuClassName="!min-w-[220px] !border-grey-100 [--anchor-gap:8px]"
                  position="bottom end"
                  className="ml-auto hidden !h-[50px] !min-w-[50px] md:flex"
                  closeOnClick={false}
                  menuItemsClassName="!text-base !font-bold"
                >
                  <button
                    type="button"
                    className="!min-w-[50px flex h-[50px] w-full items-center justify-center rounded-full border border-grey-950/[12%]"
                  >
                    <Icon
                      svg="ellipsis"
                      width={20}
                      height={20}
                      className="text-black"
                    />
                  </button>
                </Dropdown>
              ) : undefined}

              {event?.status !== "archived" && (
                <button
                  type="button"
                  className="ml-auto flex !h-[50px] !min-w-[50px] items-center justify-center rounded-full border border-grey-950/[12%] md:hidden"
                  onClick={() => {
                    handleModalOpen("moreOptionsModal");
                  }}
                >
                  <Icon
                    svg="ellipsis"
                    width={20}
                    height={20}
                    className="text-black"
                  />
                </button>
              )}
            </div>
          ) : undefined}

          <div className="border-t pt-4">
            <p className="font-bold text-primary">Share your event!</p>
            <p className="text-sm text-grey-500">
              Promote this events on socials for more reach.
            </p>

            <div className="mt-3 flex w-full items-center justify-between gap-4 rounded-[100px] bg-grey-50 p-4 text-sm font-bold text-grey-950">
              <p className="line-clamp-1 max-w-full flex-1 overflow-hidden text-ellipsis">
                {getEventUrl(event?.slug!)}
              </p>

              <CopyButton value={getEventUrl(event?.slug!)} />
            </div>
          </div>
        </div>
      </EventCard>

      <EventMoreOptionsModal
        open={modal === "moreOptionsModal" && modalOpen}
        handleClose={() => {
          handleModalClose();
        }}
        options={[
          ...(userEventPermissions.includes("editEventDetails")
            ? [moreOptions[0]]
            : []),
          ...moreOptions.slice(1),
        ]}
      />
    </>
  );
};
